import React, { useEffect, useState } from 'react';
import { Download, Share, Plus, Smartphone } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

const isStandalone = () => {
    if (typeof window === 'undefined') return false;
    return (
        window.navigator.standalone === true ||
        (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
    );
};

const isIOSSafari = () => {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    return isIOS && isSafari;
};

/**
 * Always-available "Install App" button that lives in the header.
 *
 * Behaviour by platform:
 *   - Chrome / Edge / Android with `beforeinstallprompt` support → clicking
 *     triggers the native install prompt directly.
 *   - iOS Safari → clicking opens a modal with visual instructions for the
 *     Share → Add to Home Screen flow (iOS doesn't expose a programmatic API).
 *   - Already installed as a PWA / display-mode: standalone → button is hidden.
 *   - Unsupported browser (no PWA support and not iOS) → button is hidden.
 */
export default function InstallAppButton(props) {
    const variant = props.variant || 'ghost';
    const size = props.size || 'icon';
    const className = props.className || '';
    const [installEvent, setInstallEvent] = useState(null);
    const [supported, setSupported] = useState(false);
    const [showIOSDialog, setShowIOSDialog] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        if (isStandalone()) {
            setSupported(false);
            return undefined;
        }

        // iOS path — no beforeinstallprompt event; show manual instructions.
        if (isIOSSafari()) {
            setSupported(true);
            return undefined;
        }

        // Android / Chromium path — capture the install event.
        const handler = (e) => {
            e.preventDefault();
            setInstallEvent(e);
            setSupported(true);
        };
        window.addEventListener('beforeinstallprompt', handler);

        // Hide the button if the app becomes installed while user is browsing.
        const installedHandler = () => {
            setSupported(false);
            setInstallEvent(null);
        };
        window.addEventListener('appinstalled', installedHandler);

        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
            window.removeEventListener('appinstalled', installedHandler);
        };
    }, []);

    const onClick = async () => {
        if (isIOSSafari()) {
            setShowIOSDialog(true);
            return;
        }
        if (!installEvent) return;
        installEvent.prompt();
        const choice = await installEvent.userChoice.catch(() => ({ outcome: 'dismissed' }));
        if (choice.outcome === 'accepted') {
            setInstallEvent(null);
            setSupported(false);
        }
    };

    if (!supported) return null;

    // Compact icon-only version for very tight header space; renders label on sm+
    return (
        <>
            <Button
                variant={variant}
                size={size === 'icon' ? undefined : size}
                onClick={onClick}
                data-testid="install-app-btn"
                title="Install FendanaCor as an app on this device"
                className={`gap-1.5 text-teal-700 border-teal-600 hover:bg-teal-50 hover:text-teal-800 dark:text-teal-300 dark:hover:bg-teal-900/30 ${className}`}
            >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline text-sm font-medium">Install App</span>
            </Button>

            <Dialog open={showIOSDialog} onOpenChange={setShowIOSDialog}>
                <DialogContent className="sm:max-w-md" data-testid="ios-install-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Smartphone className="w-5 h-5 text-teal-600" />
                            Install FendanaCor on your iPhone / iPad
                        </DialogTitle>
                        <DialogDescription>
                            Add FendanaCor to your Home Screen for full-screen access and push notifications.
                            Apple does not allow one-tap install from Safari, so please follow these two steps:
                        </DialogDescription>
                    </DialogHeader>

                    <ol className="space-y-4 mt-2">
                        <li className="flex items-start gap-3">
                            <div className="w-7 h-7 rounded-full bg-teal-600 text-white text-sm font-bold grid place-items-center flex-shrink-0">1</div>
                            <div className="text-sm text-slate-700 dark:text-slate-200">
                                Tap the <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded font-medium">
                                    <Share className="w-3.5 h-3.5" />Share
                                </span> icon at the bottom of your Safari window.
                            </div>
                        </li>
                        <li className="flex items-start gap-3">
                            <div className="w-7 h-7 rounded-full bg-teal-600 text-white text-sm font-bold grid place-items-center flex-shrink-0">2</div>
                            <div className="text-sm text-slate-700 dark:text-slate-200">
                                Scroll and tap <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded font-medium">
                                    <Plus className="w-3.5 h-3.5" />Add to Home Screen
                                </span>, then choose <span className="font-medium">Add</span>.
                            </div>
                        </li>
                    </ol>

                    <DialogFooter className="mt-4">
                        <Button
                            variant="outline"
                            onClick={() => setShowIOSDialog(false)}
                            data-testid="close-ios-install-dialog"
                        >
                            Got it
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
